/**
 * Admin: customer account registry (spec section 13).
 *
 * local-accounts change (2026-09-24): the registry is a mirror of what desktop devices report (plus
 * admin edits) and is never a gate. Rows carry per-device presence counts; assignments remain only as
 * optional "expected operators" metadata that nothing enforces.
 */
import { z } from 'zod';
import { AccountIdSchema, AccountProviderSchema, DeviceIdSchema, UserIdSchema } from '../../ids.js';
import { ConnectionHealthSchema } from '../../status.js';
import { CountSchema, CursorQuerySchema, EmptyRequestSchema, IsoDateTimeSchema, NoQuerySchema, RevisionSchema, defineRoute, pageOf } from '../common.js';
import { AccountReportActionSchema } from '../accounts.js';
import { AccountLifecycleSchema, AccountSchema, AssignmentSchema, ConnectionSchema } from '../entities.js';

// local-accounts change: one per-device presence row (account_device_presence), as reported by the device.
export const AccountPresenceSchema = z.object({
  accountId: AccountIdSchema,
  accountDisplayName: z.string(),
  provider: AccountProviderSchema,
  userId: UserIdSchema,
  userDisplayName: z.string(),
  deviceId: DeviceIdSchema,
  deviceName: z.string().nullable(),
  /** False once the device reported `removed_local`. */
  present: z.boolean(),
  lastAction: AccountReportActionSchema,
  lastReportedAt: IsoDateTimeSchema,
});
export type AccountPresence = z.infer<typeof AccountPresenceSchema>;
export const AccountPresenceResponseSchema = z.object({ items: z.array(AccountPresenceSchema) });

export const AccountRegistryEntrySchema = AccountSchema.extend({
  /** local-accounts change: optional expected operators (informational; nothing enforces them). */
  assignedUsers: z.array(z.object({ id: UserIdSchema, displayName: z.string(), email: z.string() })),
  /** local-accounts change: distinct users whose devices currently report this account as present. */
  operatorCount: CountSchema,
  /** local-accounts change: devices currently reporting this account as present. */
  deviceCount: CountSchema,
  /** Distinct provider connections registered (any device). */
  connectionCount: CountSchema,
  /** Locally installed copies (device × connection). */
  localCopyCount: CountSchema,
  lastReportedSyncAt: IsoDateTimeSchema.nullable(),
  worstHealth: ConnectionHealthSchema.nullable(),
  /** local-accounts change: first device report (null when the row was pre-created by an admin and never reported). */
  firstReportedAt: IsoDateTimeSchema.nullable(),
});
export type AccountRegistryEntry = z.infer<typeof AccountRegistryEntrySchema>;

export const AdminAccountListQuerySchema = CursorQuerySchema.extend({
  lifecycle: AccountLifecycleSchema.optional(),
  provider: AccountProviderSchema.optional(),
  userId: UserIdSchema.optional(),
  search: z.string().max(120).optional(),
  hasConnection: z.coerce.boolean().optional(),
});
export const AdminAccountPageSchema = pageOf(AccountRegistryEntrySchema);

export const AccountStatsResponseSchema = z.object({
  uniqueCustomers: CountSchema,
  activeAccounts: CountSchema,
  archivedAccounts: CountSchema,
  providerConnections: CountSchema,
  localCopies: CountSchema,
  byProvider: z.record(AccountProviderSchema, CountSchema),
  /** @deprecated local-accounts change: assignments are informational; kept so older callers still parse. */
  unassignedAccounts: CountSchema,
  /** local-accounts change: distinct users with at least one present presence row. */
  operators: CountSchema,
  /** local-accounts change: devices currently reporting at least one account as present. */
  devicesReporting: CountSchema,
  /** local-accounts change: registry rows with at least one present device. */
  reportedAccounts: CountSchema,
});
export type AccountStatsResponse = z.infer<typeof AccountStatsResponseSchema>;

export const AdminAccountCreateRequestSchema = z
  .object({
    displayName: z.string().min(1).max(120),
    provider: AccountProviderSchema,
    notes: z.string().max(2000).optional(),
    assignedUserIds: z.array(UserIdSchema).default([]),
  })
  .strict();
export const AdminAccountUpdateRequestSchema = z
  .object({
    displayName: z.string().min(1).max(120).optional(),
    notes: z.string().max(2000).nullable().optional(),
    provider: AccountProviderSchema.optional(),
    revision: RevisionSchema,
  })
  .strict();
/** local-accounts change: replaces the expected-operator set (informational metadata only). */
export const AdminAccountAssignRequestSchema = z.object({ userIds: z.array(UserIdSchema), revision: RevisionSchema }).strict();

export const AccountParamsSchema = z.object({ accountId: AccountIdSchema });
export const AdminAccountResponseSchema = z.object({ account: AccountRegistryEntrySchema });

export const adminAccountRoutes = {
  'admin.accounts.list': defineRoute({ id: 'admin.accounts.list', method: 'GET', path: '/admin/v1/accounts', auth: 'admin', capability: ['account.assign', 'account.register', 'audit.read'], requestSchema: AdminAccountListQuerySchema, responseSchema: AdminAccountPageSchema, description: 'Account registry as reported by devices: operator and device presence counts, connection presence, last reported sync and archive status.' }),
  'admin.accounts.stats': defineRoute({ id: 'admin.accounts.stats', method: 'GET', path: '/admin/v1/accounts/stats', auth: 'admin', capability: ['account.assign', 'account.register', 'audit.read'], requestSchema: NoQuerySchema, responseSchema: AccountStatsResponseSchema, description: 'Unique customers (registry rows), operators and devices (from presence), connections and local copies, counted separately.' }),
  'admin.accounts.get': defineRoute({ id: 'admin.accounts.get', method: 'GET', path: '/admin/v1/accounts/:accountId', auth: 'admin', capability: ['account.assign', 'account.register', 'audit.read'], paramsSchema: AccountParamsSchema, requestSchema: NoQuerySchema, responseSchema: AdminAccountResponseSchema, description: 'One registry account.' }),
  'admin.accounts.create': defineRoute({ id: 'admin.accounts.create', method: 'POST', path: '/admin/v1/accounts', auth: 'admin', capability: 'account.register', requestSchema: AdminAccountCreateRequestSchema, responseSchema: AdminAccountResponseSchema, description: 'Register a customer account with a stable id.' }),
  'admin.accounts.update': defineRoute({ id: 'admin.accounts.update', method: 'PATCH', path: '/admin/v1/accounts/:accountId', auth: 'admin', capability: 'account.register', paramsSchema: AccountParamsSchema, requestSchema: AdminAccountUpdateRequestSchema, responseSchema: AdminAccountResponseSchema, description: 'Rename / edit notes / change provider type (revision-checked).' }),
  'admin.accounts.archive': defineRoute({ id: 'admin.accounts.archive', method: 'POST', path: '/admin/v1/accounts/:accountId/archive', auth: 'admin', capability: 'account.register', paramsSchema: AccountParamsSchema, requestSchema: z.object({ revision: RevisionSchema }).strict(), responseSchema: AdminAccountResponseSchema, description: 'Archive an account.' }),
  'admin.accounts.restore': defineRoute({ id: 'admin.accounts.restore', method: 'POST', path: '/admin/v1/accounts/:accountId/restore', auth: 'admin', capability: 'account.register', paramsSchema: AccountParamsSchema, requestSchema: EmptyRequestSchema, responseSchema: AdminAccountResponseSchema, description: 'Restore an archived account.' }),
  'admin.accounts.setAssignments': defineRoute({ id: 'admin.accounts.setAssignments', method: 'PUT', path: '/admin/v1/accounts/:accountId/assignments', auth: 'admin', capability: 'account.assign', paramsSchema: AccountParamsSchema, requestSchema: AdminAccountAssignRequestSchema, responseSchema: z.object({ account: AccountRegistryEntrySchema, assignments: z.array(AssignmentSchema) }), description: 'Replace the expected-operator set (informational; nothing enforces it); emits account.assigned/unassigned events.' }),
  'admin.accounts.connections': defineRoute({ id: 'admin.accounts.connections', method: 'GET', path: '/admin/v1/accounts/:accountId/connections', auth: 'admin', capability: ['account.assign', 'account.register', 'audit.read'], paramsSchema: AccountParamsSchema, requestSchema: NoQuerySchema, responseSchema: z.object({ items: z.array(ConnectionSchema) }), description: 'Connections registered for an account (metadata only).' }),
  // local-accounts change: per-device presence rows for one account (user, device, present, last reported).
  'admin.accounts.presence': defineRoute({ id: 'admin.accounts.presence', method: 'GET', path: '/admin/v1/accounts/:accountId/presence', auth: 'admin', capability: ['account.assign', 'account.register', 'audit.read'], paramsSchema: AccountParamsSchema, requestSchema: NoQuerySchema, responseSchema: AccountPresenceResponseSchema, description: 'Presence rows reported by devices for an account (user, device, present, last action, last reported).' }),
} as const;
